import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  buildLocalizedPublicUrl,
  buildPublicMarkdownPath,
  stripLocaleFromPathname,
} from "@/config/publicSiteManifest.js";
import { getLocaleSwitchPathWithSearch } from "@/utils/publicLocale";

type ViewMode = "human" | "machine";

const PUBLIC_LOCALE_LABELS = ["PT", "EN", "FR", "ES"] as const;

const DISCOVERY_LINKS = [
  { label: "/llms.txt", href: "/llms.txt" },
  { label: "MCP SERVER CARD", href: "/.well-known/mcp/server-card.json" },
  { label: "AGENT SKILLS", href: "/.well-known/agent-skills/index.json" },
  { label: "API CATALOG", href: "/.well-known/api-catalog" },
  { label: "SITEMAP", href: "/sitemap.xml" },
] as const;

const isInteractiveTarget = (target: EventTarget | null) => {
  if (!(target instanceof HTMLElement)) return false;
  return (
    target.tagName === "INPUT" ||
    target.tagName === "TEXTAREA" ||
    target.tagName === "SELECT" ||
    target.isContentEditable
  );
};

const renderBold = (text: string): ReactNode => {
  if (!text.includes("**")) return text;
  const parts = text.split("**");
  return parts.map((part, i) =>
    i % 2 === 1 ? (
      <strong key={i} className="font-bold text-[#f4f4f0]">
        <span className="font-normal opacity-40">**</span>
        {part}
        <span className="font-normal opacity-40">**</span>
      </strong>
    ) : (
      <span key={i}>{part}</span>
    ),
  );
};

const renderInline = (text: string, keyPrefix: string): ReactNode[] => {
  const nodes: ReactNode[] = [];
  const linkRe = /\[([^\]]+)\]\(([^)\s]+)\)/g;
  let last = 0;
  let match: RegExpExecArray | null;
  let idx = 0;

  while ((match = linkRe.exec(text)) !== null) {
    if (match.index > last) {
      nodes.push(<span key={`${keyPrefix}-t${idx++}`}>{renderBold(text.slice(last, match.index))}</span>);
    }
    nodes.push(
      <a
        key={`${keyPrefix}-a${idx++}`}
        href={match[2]}
        target="_blank"
        rel="noopener noreferrer"
        className="underline decoration-white/30 underline-offset-2 hover:decoration-[#FF3300] hover:text-[#FF3300] transition-colors"
      >
        <span className="opacity-40">[</span>
        {match[1]}
        <span className="opacity-40">]({match[2]})</span>
      </a>,
    );
    last = match.index + match[0].length;
  }

  if (last < text.length) {
    nodes.push(<span key={`${keyPrefix}-t${idx++}`}>{renderBold(text.slice(last))}</span>);
  }

  return nodes;
};

const renderTableLine = (line: string, keyPrefix: string): ReactNode =>
  line.split("|").map((part, i) => (
    <span key={`${keyPrefix}-c${i}`}>
      {i > 0 && <span className="opacity-30">|</span>}
      {renderInline(part, `${keyPrefix}-c${i}`)}
    </span>
  ));

const renderMarkdownLine = (line: string, index: number): ReactNode => {
  const key = `l${index}`;
  const heading = line.match(/^(#{1,6})\s(.*)$/);
  if (heading) {
    const level = heading[1].length;
    const sizeClass =
      level === 1
        ? "text-base sm:text-lg mt-8 mb-1"
        : level === 2
          ? "text-sm sm:text-base mt-8 mb-1"
          : "text-[13px] mt-6 mb-1";
    return (
      <div key={key} className={`font-bold text-[#f4f4f0] ${sizeClass}`}>
        <span className="text-[#FF3300]">{heading[1]} </span>
        {renderInline(heading[2], key)}
      </div>
    );
  }
  if (/^>\s?/.test(line)) {
    return (
      <div key={key} className="border-l-2 border-[#FF3300]/50 pl-3 opacity-70">
        <span className="text-[#FF3300]">&gt; </span>
        {renderInline(line.replace(/^>\s?/, ""), key)}
      </div>
    );
  }
  if (/^---+\s*$/.test(line)) {
    return <div key={key} className="my-4 border-t border-white/15" aria-hidden />;
  }
  if (line.startsWith("|")) {
    const isSeparator = /^\|[\s:|-]+\|\s*$/.test(line);
    return (
      <div key={key} className={`whitespace-pre ${isSeparator ? "opacity-30" : ""}`}>
        {renderTableLine(line, key)}
      </div>
    );
  }
  if (/^-\s/.test(line)) {
    return (
      <div key={key} className="pl-1">
        <span className="text-[#FF3300]">- </span>
        {renderInline(line.slice(2), key)}
      </div>
    );
  }
  if (!line.trim()) {
    return <div key={key} className="h-3" aria-hidden />;
  }
  return (
    <div key={key} className="opacity-90">
      {renderInline(line, key)}
    </div>
  );
};

export default function PublicMachineModeShell({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { locale, basePath } = stripLocaleFromPathname(location.pathname);

  const markdownPath = buildPublicMarkdownPath(basePath, locale);
  const canonicalUrl = buildLocalizedPublicUrl(basePath, locale);
  const curlCommand = `curl -sL -H "Accept: text/markdown" ${canonicalUrl}`;

  const mode: ViewMode =
    new URLSearchParams(location.search).get("view") === "machine" ? "machine" : "human";

  const [mdState, setMdState] = useState<{ status: "loading" | "ready" | "error"; text: string }>({
    status: "loading",
    text: "",
  });
  const [copied, setCopied] = useState<"md" | "curl" | null>(null);
  const [htmlBytes, setHtmlBytes] = useState(0);
  const cacheRef = useRef(new Map<string, string>());

  const switchMode = useCallback(
    (next: ViewMode) => {
      const params = new URLSearchParams(location.search);
      if (next === "machine") {
        params.set("view", "machine");
      } else {
        params.delete("view");
      }
      const search = params.toString();
      navigate(
        { pathname: location.pathname, search: search ? `?${search}` : "", hash: location.hash },
        { replace: true },
      );
    },
    [location.pathname, location.search, location.hash, navigate],
  );

  useEffect(() => {
    document.documentElement.dataset.mode = mode;
    return () => {
      delete document.documentElement.dataset.mode;
    };
  }, [mode]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      if (event.key !== "m" && event.key !== "M") return;
      if (isInteractiveTarget(event.target)) return;
      switchMode(mode === "machine" ? "human" : "machine");
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [mode, switchMode]);

  useEffect(() => {
    if (mode !== "machine") return;
    if (htmlBytes === 0) {
      setHtmlBytes(new Blob([document.documentElement.outerHTML]).size);
    }

    const cached = cacheRef.current.get(markdownPath);
    if (cached != null) {
      setMdState({ status: "ready", text: cached });
      return;
    }

    setMdState((prev) => (prev.status === "ready" && prev.text ? prev : { status: "loading", text: "" }));
    let cancelled = false;
    fetch(markdownPath, { headers: { accept: "text/markdown" } })
      .then((response) => (response.ok ? response.text() : Promise.reject(new Error(String(response.status)))))
      .then((text) => {
        cacheRef.current.set(markdownPath, text);
        if (!cancelled) setMdState({ status: "ready", text });
      })
      .catch(() => {
        if (!cancelled) setMdState({ status: "error", text: "" });
      });
    return () => {
      cancelled = true;
    };
  }, [mode, markdownPath, htmlBytes]);

  const copyText = async (text: string, kind: "md" | "curl") => {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = text;
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      ta.remove();
    }
    setCopied(kind);
    window.setTimeout(() => setCopied(null), 1600);
  };

  const switchLanguage = (lng: string) => {
    navigate(getLocaleSwitchPathWithSearch(location.pathname, "?view=machine", lng));
  };

  const mdBytes = mdState.text ? new Blob([mdState.text]).size : 0;
  const reductionPct =
    htmlBytes > 0 && mdBytes > 0 ? (((htmlBytes - mdBytes) / htmlBytes) * 100).toFixed(1) : null;
  const tokenEstimate = Math.ceil(mdState.text.length / 4);

  const toggleButton = (target: ViewMode, label: string) => {
    const isActive = mode === target;
    return (
      <button
        type="button"
        data-switch-mode={target}
        onClick={() => switchMode(target)}
        aria-pressed={isActive}
        className={`flex items-center gap-2 px-2 py-1 transition-colors cursor-pointer ${
          isActive ? "text-white" : "text-white/50 hover:text-white/90"
        }`}
      >
        <span
          className={`inline-block size-[6px] rounded-full outline outline-1 outline-offset-2 ${
            isActive ? "bg-[#FF3300] outline-[#FF3300]/60" : "outline-white/50"
          }`}
          aria-hidden
        />
        {label}
      </button>
    );
  };

  return (
    <>
      <div
        data-human-view={mode === "human" ? "active" : "hidden"}
        className={mode === "machine" ? "hidden" : undefined}
        aria-hidden={mode === "machine"}
      >
        {children}
        {mode === "human" && <div className="h-14" aria-hidden />}
      </div>

      {mode === "machine" && (
        <div className="fixed inset-0 z-[60] overflow-y-auto bg-[#050505] font-mono text-[#f4f4f0] selection:bg-[#FF3300] selection:text-white">
          <header className="sticky top-0 z-10 border-b border-white/10 bg-[#050505]/90 backdrop-blur-md">
            <div className="mx-auto flex max-w-[1100px] items-center justify-between gap-4 px-4 py-3 sm:px-6">
              <div className="flex min-w-0 items-center gap-3">
                <button
                  type="button"
                  onClick={() => switchMode("human")}
                  className="shrink-0 font-bold uppercase tracking-widest text-xs hover:text-[#FF3300] transition-colors"
                >
                  BABETE
                </button>
                <span className="hidden truncate text-[10px] uppercase tracking-widest text-[#FF3300] sm:inline">
                  {t("machineMode.badge")}
                </span>
              </div>
              <div className="flex shrink-0 items-center gap-3 text-[10px] uppercase tracking-widest">
                <div className="flex items-center gap-1">
                  {PUBLIC_LOCALE_LABELS.map((l) => (
                    <button
                      key={l}
                      type="button"
                      onClick={() => switchLanguage(l.toLowerCase())}
                      className={`px-1.5 py-1 transition-colors cursor-pointer ${
                        locale === l.toLowerCase() ? "font-bold text-[#FF3300]" : "text-white/50 hover:text-white"
                      }`}
                    >
                      {l}
                    </button>
                  ))}
                </div>
                <a
                  href={markdownPath}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="border border-white/20 px-2 py-1 text-white/70 transition-colors hover:border-[#FF3300] hover:text-[#FF3300]"
                >
                  {t("machineMode.raw_md")}
                </a>
              </div>
            </div>
          </header>

          <div className="mx-auto max-w-[1100px] px-4 pb-28 sm:px-6">
            <section className="mt-6 border border-white/15">
              <div className="border-b border-white/15 px-4 py-2 text-[10px] uppercase tracking-widest text-[#FF3300]">
                {t("machineMode.telemetry_title")}
              </div>
              <div className="grid grid-cols-2 gap-px bg-white/10 text-[10px] sm:grid-cols-4 sm:text-xs">
                <div className="bg-[#050505] px-4 py-3">
                  <div className="uppercase tracking-widest opacity-50">HTML DOM</div>
                  <div className="mt-1">{(htmlBytes / 1024).toFixed(1)} KB</div>
                </div>
                <div className="bg-[#050505] px-4 py-3">
                  <div className="uppercase tracking-widest opacity-50">MARKDOWN</div>
                  <div className="mt-1 text-[#FF3300]">
                    {(mdBytes / 1024).toFixed(1)} KB{reductionPct ? ` (-${reductionPct}%)` : ""}
                  </div>
                </div>
                <div className="bg-[#050505] px-4 py-3">
                  <div className="uppercase tracking-widest opacity-50">{t("machineMode.tokens_est")}</div>
                  <div className="mt-1">~{tokenEstimate.toLocaleString()}</div>
                </div>
                <div className="bg-[#050505] px-4 py-3">
                  <div className="uppercase tracking-widest opacity-50">NEGOTIATION</div>
                  <div className="mt-1">text/markdown · Vary: Accept</div>
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-2 border-t border-white/15 px-4 py-3">
                <button
                  type="button"
                  onClick={() => copyText(mdState.text, "md")}
                  disabled={!mdState.text}
                  className="border border-white/20 px-3 py-1.5 text-[10px] uppercase tracking-widest transition-colors hover:border-[#FF3300] hover:text-[#FF3300] disabled:opacity-40 cursor-pointer"
                >
                  {copied === "md" ? t("machineMode.copied") : t("machineMode.copy_md")}
                </button>
                <button
                  type="button"
                  onClick={() => copyText(curlCommand, "curl")}
                  className="border border-white/20 px-3 py-1.5 text-[10px] uppercase tracking-widest transition-colors hover:border-[#FF3300] hover:text-[#FF3300] cursor-pointer"
                >
                  {copied === "curl" ? t("machineMode.copied") : t("machineMode.copy_curl")}
                </button>
                <code className="min-w-0 flex-1 truncate px-2 text-[10px] opacity-40">{curlCommand}</code>
              </div>
              <div className="border-t border-white/15 px-4 py-3">
                <div className="text-[10px] uppercase tracking-widest opacity-50">
                  {t("machineMode.discovery_label")}
                </div>
                <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-[10px] uppercase tracking-widest">
                  {DISCOVERY_LINKS.map((link) => (
                    <a
                      key={link.href}
                      href={link.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-white/60 underline decoration-white/20 underline-offset-2 transition-colors hover:text-[#FF3300]"
                    >
                      {link.label}
                    </a>
                  ))}
                </div>
                <div className="mt-3 text-[10px] uppercase tracking-widest opacity-50">
                  {t("machineMode.webmcp_label")}
                </div>
                <div className="mt-1 text-[10px] text-white/60">
                  search_urban_regulations (RGEU/RJUE/SIMPLEX) · search_condo_law (Código Civil Art. 1414.º–1438.º-A)
                </div>
              </div>
            </section>

            <article className="mt-8 border border-white/15 bg-[#0a0a0a] px-4 py-6 sm:px-8 sm:py-8">
              {mdState.status === "loading" && (
                <div className="text-xs uppercase tracking-widest opacity-50">{t("machineMode.loading")}</div>
              )}
              {mdState.status === "error" && (
                <div className="text-xs uppercase tracking-widest text-[#FF3300]">{t("machineMode.error")}</div>
              )}
              {mdState.status === "ready" && (
                <div className="overflow-x-auto text-[11px] leading-[1.8] sm:text-[13px]">
                  {mdState.text.split("\n").map((line, i) => renderMarkdownLine(line, i))}
                </div>
              )}
            </article>
          </div>
        </div>
      )}

      <div className="fixed bottom-[calc(1.5rem_+_var(--bottom-chrome-offset,0px))] left-1/2 z-[70] -translate-x-1/2 transition-[bottom] duration-300 ease-out motion-reduce:transition-none md:bottom-6">
        <div
          role="group"
          aria-label={t("machineMode.toggle_label")}
          className="flex items-center gap-1 rounded-sm border border-white/20 bg-[#0a0a0a]/90 p-1 font-mono text-[10px] uppercase tracking-widest text-white shadow-2xl backdrop-blur-md sm:text-xs"
        >
          {toggleButton("human", t("machineMode.human"))}
          {toggleButton("machine", t("machineMode.machine"))}
        </div>
      </div>
    </>
  );
}
